// src/components/IngoZifiteAmacyimbirane.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from "../utils/axiosInstance";
import { toast } from 'react-toastify';

import { getApiBase } from "../utils/useApiBase";
const API_BASE = getApiBase();

const statusOptions = [
  { value: 'Biracyariho', label: 'Biracyariho', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
  { value: 'Biri_mu_nzira', label: 'Biri mu nzira', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
  { value: 'Byakemutse', label: 'Byakemutse', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
];

const usePlatform = () => {
  const [platform, setPlatform] = useState({
    isAndroid: false,
    isIOS: false,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isPWA: false,
    hasNotch: false,
    supportsTouch: false,
    screenWidth: 0,
    screenHeight: 0,
    orientation: 'portrait',
  });

  useEffect(() => {
    const updatePlatform = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isAndroid = /android/.test(ua);
      const isIOS = /iphone|ipad|ipod/.test(ua);
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isMobile = /mobile/.test(ua) || screenWidth < 768;
      const isTablet = /tablet|ipad/.test(ua) || (screenWidth >= 768 && screenWidth < 1024);
      const isDesktop = !isMobile && !isTablet;
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      const hasNotch = screenHeight >= 812 && screenWidth >= 375;
      const supportsTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const orientation = screenHeight > screenWidth ? 'portrait' : 'landscape';

      setPlatform({
        isAndroid,
        isIOS,
        isMobile,
        isTablet,
        isDesktop,
        isPWA,
        hasNotch,
        supportsTouch,
        screenWidth,
        screenHeight,
        orientation,
      });

      document.body.className = document.body.className.replace(/platform-\w+|screen-\w+|orientation-\w+/g, '');
      document.body.classList.add(`platform-${isAndroid ? 'android' : isIOS ? 'ios' : 'web'}`);
      document.body.classList.add(
        `screen-${screenWidth < 480 ? 'xs' : screenWidth < 768 ? 'sm' : screenWidth < 1024 ? 'md' : 'lg'}`
      );
      document.body.classList.add(`orientation-${orientation}`);
      if (isMobile) document.body.classList.add('platform-mobile');
      if (isTablet) document.body.classList.add('platform-tablet');
      if (isPWA) document.body.classList.add('platform-pwa');
      if (hasNotch) document.body.classList.add('has-notch');
    };

    updatePlatform();
    window.addEventListener('resize', updatePlatform);
    window.addEventListener('orientationchange', updatePlatform);

    return () => {
      window.removeEventListener('resize', updatePlatform);
      window.removeEventListener('orientationchange', updatePlatform);
    };
  }, []);

  return platform;
};

const useFormValidation = (formData) => {
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const newErrors = {};

    if (!formData.amazina_y_umukuru_w_urugo?.trim()) {
      newErrors.amazina_y_umukuru_w_urugo = "Andika amazina y'umukuru w'urugo";
    }
    if (!formData.itariki) {
      newErrors.itariki = 'Hitamo itariki';
    }
    if (!formData.amacyimbirane_bafitanye?.trim()) {
      newErrors.amacyimbirane_bafitanye = 'Sobanura amacyimbirane bafitanye';
    }
    if (!formData.impamvu_y_amacyimbirane?.trim()) {
      newErrors.impamvu_y_amacyimbirane = "Andika impamvu y'amacyimbirane";
    }
    if (!formData.imiterere) {
      newErrors.imiterere = "Hitamo imiterere y'ikibazo";
    }

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  return { errors, isValid };
};

const useAutoSave = (formData, delay = 3000) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (!formData.amazina_y_umukuru_w_urugo && !formData.amacyimbirane_bafitanye) return;

    const timer = setTimeout(() => {
      setIsSaving(true);
      try {
        localStorage.setItem(
          'mk_draft_form',
          JSON.stringify({
            ...formData,
            timestamp: Date.now(),
          })
        );
        setLastSaved(new Date());
      } catch (err) {
        console.warn('Could not save draft:', err);
      }
      setIsSaving(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [formData, delay]);

  const loadDraft = useCallback(() => {
    try {
      const draft = localStorage.getItem('mk_draft_form');
      if (draft) {
        const parsedDraft = JSON.parse(draft);
        const age = Date.now() - parsedDraft.timestamp;
        if (age < 24 * 60 * 60 * 1000) {
          return parsedDraft;
        }
      }
    } catch (err) {
      console.warn('Could not load draft:', err);
    }
    return null;
  }, []);

  const clearDraft = () => {
    localStorage.removeItem('mk_draft_form');
    setLastSaved(null);
  };

  return { isSaving, lastSaved, loadDraft, clearDraft };
};

const usePullToRefresh = (onRefresh) => {
  const [isPulling, setIsPulling] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (window.scrollY === 0 && startY.current > 0) {
        currentY.current = e.touches[0].clientY;
        const pullDistance = currentY.current - startY.current;
        if (pullDistance > 0 && pullDistance < 100) {
          setIsPulling(true);
        }
      }
    };

    const handleTouchEnd = async () => {
      if (isPulling && currentY.current - startY.current > 60) {
        await onRefresh();
      }
      setIsPulling(false);
      startY.current = 0;
      currentY.current = 0;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, onRefresh]);

  return isPulling;
};

// DELETE CONFIRMATION MODAL COMPONENT
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, recordName, isDeleting }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="mk-delete-overlay">
      <div className="mk-delete-modal">
        <div className="mk-delete-header">
          <div className="mk-delete-icon">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <h3 className="mk-delete-title">Siba Amakimbirane</h3>
        </div>
        
        <div className="mk-delete-content">
          <p className="mk-delete-message">
            Uzi neza ko ushaka gusiba amakimbirane ya <strong>{recordName}</strong>?
          </p>
          <div className="mk-delete-warning">
            <i className="bi bi-info-circle"></i>
            <span>Iki gikorwa ntishobora gusubizwa!</span>
          </div>
        </div>
        
        <div className="mk-delete-actions">
          <button 
            className="mk-delete-btn mk-delete-cancel mk-ripple" 
            onClick={onClose}
            disabled={isDeleting}
          >
            <i className="bi bi-x-lg"></i>
            <span>Hagarika</span>
          </button>
          <button 
            className="mk-delete-btn mk-delete-confirm mk-ripple" 
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="mk-delete-spinner"></div>
                <span>Gusiba...</span>
              </>
            ) : (
              <>
                <i className="bi bi-trash3"></i>
                <span>Siba</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const GraphicalStats = ({ records }) => {
  const total = records.length;
  const byStatus = statusOptions.map((opt) => ({
    ...opt,
    count: records.filter((r) => r.imiterere === opt.value).length,
    percentage: total > 0 ? Math.round((records.filter((r) => r.imiterere === opt.value).length / total) * 100) : 0,
  }));

  return (
    <div className="mk-stats-wrapper">
      <div className="mk-stats-grid">
        {byStatus.map((stat) => (
          <div key={stat.value} className="mk-stat-card mk-ripple" style={{ borderColor: stat.color }}>
            <div className="mk-stat-header">
              <div className="mk-stat-icon" style={{ background: stat.bgColor, color: stat.color }}>
                <i
                  className={`bi ${
                    stat.value === 'Biracyariho'
                      ? 'bi-exclamation-circle'
                      : stat.value === 'Biri_mu_nzira'
                      ? 'bi-clock-history'
                      : 'bi-check-circle'
                  }`}
                />
              </div>
              <span className="mk-stat-badge" style={{ background: stat.bgColor, color: stat.color }}>
                {stat.percentage}%
              </span>
            </div>
            <div className="mk-stat-value" style={{ color: stat.color }}>
              {stat.count}
            </div>
            <div className="mk-stat-label">{stat.label}</div>
            <div className="mk-stat-progress">
              <div
                className="mk-stat-progress-bar"
                style={{ width: `${stat.percentage}%`, background: stat.color }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mk-chart-section">
        <h3 className="mk-chart-title">
          <i className="bi bi-bar-chart" />
          Ibimenyetso mu Gice
        </h3>
        <div className="mk-chart-container">
          {byStatus.map((stat) => (
            <div key={stat.value} className="mk-chart-row">
              <div className="mk-chart-label">
                <span className="mk-chart-dot" style={{ background: stat.color }} />
                <span className="mk-chart-label-text">{stat.label}</span>
              </div>
              <div className="mk-chart-bar-wrapper">
                <div
                  className="mk-chart-bar"
                  style={{
                    width: `${stat.percentage}%`,
                    background: `linear-gradient(90deg, ${stat.color}, ${stat.color}88)`,
                  }}
                >
                  <span className="mk-chart-value">{stat.count}</span>
                </div>
              </div>
              <div className="mk-chart-percentage">{stat.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mk-pie-section">
        <h3 className="mk-chart-title">
          <i className="bi bi-pie-chart" />
          Gacekano
        </h3>
        <div className="mk-pie-container">
          <div className="mk-pie-chart">
            <svg viewBox="0 0 100 100" className="mk-pie-svg">
              {total === 0 ? (
                <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="20" />
              ) : (
                byStatus.reduce(
                  (acc, stat) => {
                    const percentage = stat.percentage;
                    const startAngle = acc.currentAngle;
                    const endAngle = acc.currentAngle + percentage * 3.6;

                    const x1 = 50 + 40 * Math.cos(((startAngle - 90) * Math.PI) / 180);
                    const y1 = 50 + 40 * Math.sin(((startAngle - 90) * Math.PI) / 180);
                    const x2 = 50 + 40 * Math.cos(((endAngle - 90) * Math.PI) / 180);
                    const y2 = 50 + 40 * Math.sin(((endAngle - 90) * Math.PI) / 180);

                    const largeArc = percentage > 50 ? 1 : 0;

                    acc.elements.push(
                      <path
                        key={stat.value}
                        d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={stat.color}
                        className="mk-pie-slice"
                      />
                    );

                    acc.currentAngle = endAngle;
                    return acc;
                  },
                  { elements: [], currentAngle: 0 }
                ).elements
              )}
              <circle cx="50" cy="50" r="25" fill="var(--bg-body, #f0f4f8)" />
            </svg>
            <div className="mk-pie-center">
              <span className="mk-pie-total">{total}</span>
              <span className="mk-pie-label">Yose</span>
            </div>
          </div>
          <div className="mk-pie-legend">
            {byStatus.map((stat) => (
              <div key={stat.value} className="mk-pie-legend-item">
                <span className="mk-legend-color" style={{ background: stat.color }} />
                <span className="mk-legend-label">{stat.label}</span>
                <span className="mk-legend-value" style={{ color: stat.color }}>
                  {stat.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileNav = ({ isOpen, onToggle, activeTab, onTabChange, isPulling }) => (
  <>
    <div className={`mk-mobile-nav ${isPulling ? 'pulling' : ''}`}>
      <button className="mk-nav-brand mk-ripple" onClick={onToggle}>
        <div className="mk-nav-logo">
          <i className="bi bi-house-heart" />
        </div>
        <div className="mk-nav-title">
          <span className="mk-nav-main">Amacyimbirane</span>
          <span className="mk-nav-sub">Ingo zifite</span>
        </div>
      </button>
      <div className="mk-nav-actions">
        <button className="mk-nav-btn mk-ripple" onClick={onToggle}>
          <i className="bi bi-list" />
        </button>
      </div>
    </div>

    {isPulling && (
      <div className="mk-pull-indicator">
        <div className="mk-pull-spinner" />
        <p className="mk-pull-text">Gukosora amakuru...</p>
      </div>
    )}

    {isOpen && (
      <>
        <div className="mk-nav-overlay" onClick={onToggle} />
        <div className="mk-nav-drawer">
          <div className="mk-drawer-header">
            <span>Menu</span>
            <i className="bi bi-x-lg" onClick={onToggle} />
          </div>
          <div className="mk-drawer-content">
            <button
              className={`mk-drawer-item mk-ripple ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => {
                onTabChange('stats');
                onToggle();
              }}
            >
              <i className="bi bi-graph-up" />
              <span>Ibisobanuro</span>
              <i className="bi bi-chevron-right mk-drawer-arrow" />
            </button>
            <button
              className={`mk-drawer-item mk-ripple ${activeTab === 'list' ? 'active' : ''}`}
              onClick={() => {
                onTabChange('list');
                onToggle();
              }}
            >
              <i className="bi bi-list-ul" />
              <span>Lisiti y'ibibazo</span>
              <i className="bi bi-chevron-right mk-drawer-arrow" />
            </button>
            <button
              className={`mk-drawer-item mk-ripple ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => {
                onTabChange('add');
                onToggle();
              }}
            >
              <i className="bi bi-plus-circle" />
              <span>Andika Amakimbirane</span>
              <i className="bi bi-chevron-right mk-drawer-arrow" />
            </button>
          </div>
        </div>
      </>
    )}
  </>
);

const ConflictCard = ({ record, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const status = statusOptions.find((s) => s.value === record.imiterere);

  return (
    <div className="mk-card mk-ripple">
      <div className="mk-card-header">
        <div className="mk-card-user">
          <div className="mk-card-avatar" style={{ background: status?.bgColor, color: status?.color }}>
            <i className="bi bi-person-fill" />
          </div>
          <div className="mk-card-user-info">
            <h4 title={record.amazina_y_umukuru_w_urugo}>{record.amazina_y_umukuru_w_urugo}</h4>
            <span className="mk-card-date">
              <i className="bi bi-calendar3" />
              {record.itariki ? new Date(record.itariki).toLocaleDateString('rw-RW') : '---'}
            </span>
          </div>
        </div>
        <span
          className="mk-card-status"
          style={{
            background: status?.bgColor,
            color: status?.color,
            borderColor: status?.color,
          }}
        >
          {status?.label}
        </span>
      </div>

      <div className="mk-card-body">
        <div className="mk-card-item">
          <label>
            <i className="bi bi-people" /> Amacyimbirane
          </label>
          <p className={!isExpanded ? 'mk-text-clamp' : ''} title={record.amacyimbirane_bafitanye}>
            {record.amacyimbirane_bafitanye || '---'}
          </p>
        </div>

        <div className="mk-card-item">
          <label>
            <i className="bi bi-question-circle" /> Impamvu
          </label>
          <p className={!isExpanded ? 'mk-text-clamp' : ''} title={record.impamvu_y_amacyimbirane}>
            {record.impamvu_y_amacyimbirane || '---'}
          </p>
        </div>

        {record.icyakorwa_ngo_acyemuke && (
          <div className="mk-card-item mk-card-solution">
            <label>
              <i className="bi bi-lightbulb" /> Umwanzuro
            </label>
            <p className={!isExpanded ? 'mk-text-clamp' : ''} title={record.icyakorwa_ngo_acyemuke}>
              {record.icyakorwa_ngo_acyemuke}
            </p>
          </div>
        )}

        {(record.amacyimbirane_bafitanye?.length > 100 ||
          record.impamvu_y_amacyimbirane?.length > 100 ||
          record.icyakorwa_ngo_acyemuke?.length > 100) && (
          <button className="mk-expand-btn" onClick={() => setIsExpanded(!isExpanded)}>
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
            {isExpanded ? 'Funga' : 'Soma byose'}
          </button>
        )}
      </div>

      <div className="mk-card-actions">
        <button className="mk-btn mk-btn-edit mk-ripple" onClick={() => onEdit(record)}>
          <i className="bi bi-pencil-square" />
          <span>Hindura</span>
        </button>
        <button className="mk-btn mk-btn-delete mk-ripple" onClick={() => onDelete(record.id, record.amazina_y_umukuru_w_urugo)}>
          <i className="bi bi-trash3" />
          <span>Siba</span>
        </button>
      </div>
    </div>
  );
};

const ResponsiveForm = ({ formData, onChange, onSubmit, onCancel, editingId, platform, loading, householdHeads }) => {
  const { errors, isValid } = useFormValidation(formData);
  const { isSaving, lastSaved, loadDraft, clearDraft } = useAutoSave(formData);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const formRef = useRef();

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  useEffect(() => {
    if (!editingId) {
      const draft = loadDraft();
      if (draft && Object.values(draft).some((v) => v && v !== 'Biracyariho')) {
        setShowDraftDialog(true);
      }
    }
  }, [editingId, loadDraft]);

  useEffect(() => {
    if (platform.isMobile && formRef.current) {
      const firstInput = formRef.current.querySelector('input[type="text"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 300);
      }
    }
  }, [platform.isMobile]);

  const handleDraftAccept = () => {
    const draft = loadDraft();
    if (draft) {
      Object.keys(draft).forEach((key) => {
        if (key !== 'timestamp' && Object.prototype.hasOwnProperty.call(formData, key)) {
          onChange({ target: { name: key, value: draft[key] } });
        }
      });
    }
    setShowDraftDialog(false);
  };

  const handleDraftReject = () => {
    clearDraft();
    setShowDraftDialog(false);
  };

  const handleSubmitWithClearDraft = (e) => {
    e.preventDefault();
    if (isValid) {
      clearDraft();
      onSubmit(e);
    } else {
      setAttemptedSubmit(true);
    }
  };

  const getFieldError = (fieldName) => ((touched[fieldName] || attemptedSubmit) ? errors[fieldName] : undefined);

  return (
    <>
      {showDraftDialog && (
        <div className="mk-draft-overlay">
          <div className="mk-draft-dialog">
            <div className="mk-draft-header">
              <i className="bi bi-clock-history" />
              <h3>Amakuru yarasigaye</h3>
            </div>
            <p>Harimo amakuru warandikaga mbere. Urashaka kuyagarura?</p>
            <div className="mk-draft-actions">
              <button className="mk-draft-btn mk-draft-reject" onClick={handleDraftReject}>
                <i className="bi bi-trash3" />
                Siba
              </button>
              <button className="mk-draft-btn mk-draft-accept" onClick={handleDraftAccept}>
                <i className="bi bi-arrow-clockwise" />
                Garura
              </button>
            </div>
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmitWithClearDraft} className="mk-form">
        <div className="mk-form-header">
          <div className="mk-form-progress">
            <div
              className="mk-progress-bar"
              style={{ width: `${(Object.values(formData).filter((v) => v).length / 6) * 100}%` }}
            />
          </div>
          {lastSaved && !platform.isDesktop && (
            <div className="mk-auto-save-status">
              <i className={`bi ${isSaving ? 'bi-cloud-arrow-up' : 'bi-cloud-check'}`} />
              <span>
                {isSaving
                  ? 'Bika...'
                  : `Byabitswe ${lastSaved.toLocaleTimeString('rw-RW', { hour: '2-digit', minute: '2-digit' })}`}
              </span>
            </div>
          )}
        </div>

        <div className="mk-form-grid">
          <div className="mk-input-group">
            <label>
              <i className="bi bi-person" />
              <span>Amazina y'Umukuru w'Urugo</span>
              <span className="mk-required">*</span>
            </label>
            <div className="mk-input-wrapper">
              <select
                name="citizen_id"
                value={formData.citizen_id || ''}
                onChange={onChange}
                onBlur={handleBlur}
                required
                className={getFieldError('amazina_y_umukuru_w_urugo') ? 'error' : ''}
              >
                <option value="">-- Hitamo umukuru w'urugo --</option>
                {(householdHeads || []).map((h) => (
                  <option key={h.umuturage_id} value={h.umuturage_id}>
                    {h.full_name}
                  </option>
                ))}
              </select>
              {getFieldError('amazina_y_umukuru_w_urugo') && (
                <div className="mk-field-error">
                  <i className="bi bi-exclamation-triangle" />
                  {getFieldError('amazina_y_umukuru_w_urugo')}
                </div>
              )}
            </div>
          </div>

          <div className="mk-input-group">
            <label>
              <i className="bi bi-calendar3" />
              <span>Itariki</span>
              <span className="mk-required">*</span>
            </label>
            <div className="mk-input-wrapper">
              <input
                type="date"
                name="itariki"
                value={formData.itariki}
                onChange={onChange}
                onBlur={handleBlur}
                required
                max={new Date().toISOString().split('T')[0]}
                className={getFieldError('itariki') ? 'error' : ''}
              />
              {getFieldError('itariki') && (
                <div className="mk-field-error">
                  <i className="bi bi-exclamation-triangle" />
                  {getFieldError('itariki')}
                </div>
              )}
            </div>
          </div>

          <div className="mk-input-group mk-full">
            <label>
              <i className="bi bi-people" />
              <span>Amacyimbirane bafitanye</span>
              <span className="mk-required">*</span>
            </label>
            <div className="mk-input-wrapper">
              <textarea
                name="amacyimbirane_bafitanye"
                rows={platform.isMobile ? '4' : '3'}
                value={formData.amacyimbirane_bafitanye}
                onChange={onChange}
                onBlur={handleBlur}
                required
                placeholder="Sobanura neza amacyimbirane bafitanye..."
                inputMode="text"
                maxLength="1000"
                className={getFieldError('amacyimbirane_bafitanye') ? 'error' : ''}
              />
              <div className="mk-textarea-footer">
                <div className="mk-char-count">{formData.amacyimbirane_bafitanye.length}/1000</div>
                {getFieldError('amacyimbirane_bafitanye') && (
                  <div className="mk-field-error">
                    <i className="bi bi-exclamation-triangle" />
                    {getFieldError('amacyimbirane_bafitanye')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mk-input-group mk-full">
            <label>
              <i className="bi bi-question-circle" />
              <span>Impamvu y'amacyimbirane</span>
              <span className="mk-required">*</span>
            </label>
            <div className="mk-input-wrapper">
              <textarea
                name="impamvu_y_amacyimbirane"
                rows={platform.isMobile ? '4' : '3'}
                value={formData.impamvu_y_amacyimbirane}
                onChange={onChange}
                onBlur={handleBlur}
                required
                placeholder="Andika impamvu nyamukuru..."
                inputMode="text"
                maxLength="1000"
                className={getFieldError('impamvu_y_amacyimbirane') ? 'error' : ''}
              />
              <div className="mk-textarea-footer">
                <div className="mk-char-count">{formData.impamvu_y_amacyimbirane.length}/1000</div>
                {getFieldError('impamvu_y_amacyimbirane') && (
                  <div className="mk-field-error">
                    <i className="bi bi-exclamation-triangle" />
                    {getFieldError('impamvu_y_amacyimbirane')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mk-input-group mk-full">
            <label>
              <i className="bi bi-lightbulb" />
              <span>Icyakorwa ngo icyemuke</span>
              <span className="mk-optional">(Ntabwo ari ngombwa)</span>
            </label>
            <div className="mk-input-wrapper">
              <textarea
                name="icyakorwa_ngo_acyemuke"
                rows={platform.isMobile ? '4' : '3'}
                value={formData.icyakorwa_ngo_acyemuke}
                onChange={onChange}
                placeholder="Sobanura icyakorwa..."
                inputMode="text"
                maxLength="1000"
              />
              <div className="mk-char-count">{formData.icyakorwa_ngo_acyemuke.length}/1000</div>
            </div>
          </div>

          <div className="mk-input-group mk-full">
            <label>
              <i className="bi bi-flag" />
              <span>Imiterere y'icyo kibazo</span>
              <span className="mk-required">*</span>
            </label>
            <div className="mk-radio-group">
              {statusOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`mk-radio-label mk-ripple ${formData.imiterere === opt.value ? 'active' : ''}`}
                  style={{
                    '--radio-color': opt.color,
                    '--radio-bg': opt.bgColor,
                  }}
                >
                  <input
                    type="radio"
                    name="imiterere"
                    value={opt.value}
                    checked={formData.imiterere === opt.value}
                    onChange={onChange}
                  />
                  <div className="mk-radio-icon">
                    <i
                      className={`bi ${
                        opt.value === 'Biracyariho'
                          ? 'bi-exclamation-circle'
                          : opt.value === 'Biri_mu_nzira'
                          ? 'bi-clock-history'
                          : 'bi-check-circle'
                      }`}
                    />
                  </div>
                  <div className="mk-radio-content">
                    <span className="mk-radio-text">{opt.label}</span>
                    <span className="mk-radio-desc">
                      {opt.value === 'Biracyariho'
                        ? 'Ikibazo kigikiriho'
                        : opt.value === 'Biri_mu_nzira'
                        ? 'Kigiye gukemuka'
                        : 'Cyakemutse neza'}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            {getFieldError('imiterere') && (
              <div className="mk-field-error">
                <i className="bi bi-exclamation-triangle" />
                {getFieldError('imiterere')}
              </div>
            )}
          </div>
        </div>

        <div className="mk-form-actions">
          <button type="button" className="mk-btn-cancel mk-ripple" onClick={onCancel}>
            <i className="bi bi-x-lg" />
            <span>Hagarika</span>
          </button>
          <button
            type="submit"
            className={`mk-btn-submit mk-ripple ${!isValid ? 'disabled' : ''}`}
            disabled={!isValid || loading}
          >
            {loading ? (
              <>
                <div className="mk-loading-spinner" />
                <span>Gutegereza...</span>
              </>
            ) : (
              <>
                <i className={`bi ${editingId ? 'bi-check-lg' : 'bi-save'}`} />
                <span>{editingId ? 'Vugurura' : 'Bika Amakuru'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
};

export default function IngoZifiteAmacyimbirane() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [submitLoading, setSubmitLoading] = useState(false);

  // DELETE MODAL STATE
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
    recordName: '',
    isDeleting: false,
  });

  const platform = usePlatform();

  const [formData, setFormData] = useState({
    amazina_y_umukuru_w_urugo: '',
    amacyimbirane_bafitanye: '',
    impamvu_y_amacyimbirane: '',
    icyakorwa_ngo_acyemuke: '',
    imiterere: 'Biracyariho',
    itariki: '',
    citizen_id: '',
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Household heads already registered under Abaturage, for the
  // auto-populate dropdown instead of hand-typing a name.
  const [householdHeads, setHouseholdHeads] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_BASE}/isibo/household-heads`, config)
      .then((res) => setHouseholdHeads(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHouseholdHeads([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/amakimbirane`, config);
      setRecords(res.data || []);
    } catch (err) {
      toast.error('Ntibyashobotse kubona amakimbirane');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) fetchRecords();
  }, [fetchRecords, token]);

  const isPulling = usePullToRefresh(fetchRecords);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'citizen_id') {
      const head = householdHeads.find((h) => String(h.umuturage_id) === String(value));
      setFormData((prev) => ({
        ...prev,
        citizen_id: value,
        amazina_y_umukuru_w_urugo: head ? head.full_name : '',
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      amazina_y_umukuru_w_urugo: '',
      amacyimbirane_bafitanye: '',
      impamvu_y_amacyimbirane: '',
      icyakorwa_ngo_acyemuke: '',
      imiterere: 'Biracyariho',
      itariki: '',
      citizen_id: '',
    });
    setIsModalOpen(false);
    setActiveTab('list');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE}/amakimbirane/${editingId}`, formData, config);
        toast.success('Byavuguruwe neza!');
      } else {
        await axios.post(`${API_BASE}/amakimbirane`, formData, config);
        toast.success('Byanditswe neza!');
      }
      resetForm();
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ikosa ryabaye');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormData({
      amazina_y_umukuru_w_urugo: record.amazina_y_umukuru_w_urugo || '',
      amacyimbirane_bafitanye: record.amacyimbirane_bafitanye || '',
      impamvu_y_amacyimbirane: record.impamvu_y_amacyimbirane || '',
      icyakorwa_ngo_acyemuke: record.icyakorwa_ngo_acyemuke || '',
      imiterere: record.imiterere || 'Biracyariho',
      itariki: record.itariki ? record.itariki.split('T')[0] : '',
      citizen_id: record.citizen_id || '',
    });
    setIsModalOpen(true);
    setActiveTab('add');
  };

  // UPDATED DELETE HANDLERS
  const handleDeleteRequest = (id, name) => {
    setDeleteModal({
      isOpen: true,
      recordId: id,
      recordName: name,
      isDeleting: false,
    });
  };

  const handleDeleteConfirm = async () => {
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    try {
      await axios.delete(`${API_BASE}/amakimbirane/${deleteModal.recordId}`, config);
      toast.success('Byasibwe neza!');
      fetchRecords();
      setDeleteModal({ isOpen: false, recordId: null, recordName: '', isDeleting: false });
    } catch (err) {
      toast.error('Ntibyashobotse gusiba');
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, recordId: null, recordName: '', isDeleting: false });
  };

  useEffect(() => {
    if (isModalOpen || deleteModal.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen, deleteModal.isOpen]);

  const filteredRecords = records.filter((r) => (filterStatus === 'all' ? true : r.imiterere === filterStatus));

  return (
    <div className="mk-container">
      <MkStyles />

      {isMobile && (
        <MobileNav
          isOpen={isNavOpen}
          onToggle={() => setIsNavOpen(!isNavOpen)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isPulling={isPulling}
        />
      )}

      {!isMobile && (
        <header className="mk-header">
          <div className="mk-header-info">
            <h1>
              <i className="bi bi-house-heart" />
              Amacyimbirane mu Ngo
            </h1>
            <p>Gucunga no gukemura ibibazo mu mudugudu</p>
          </div>
          <button
            className="mk-add-btn mk-ripple"
            onClick={() => {
              setIsModalOpen(true);
              setActiveTab('add');
            }}
          >
            <i className="bi bi-plus-lg" />
            <span>Andika Amacyimbirane</span>
          </button>
        </header>
      )}

      {!isMobile && (
        <div className="mk-filter-bar">
          <div className="mk-search-wrapper">
            <i className="bi bi-funnel" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Ibibazo Byose</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mk-results-count">
            <span className="mk-count-number">{filteredRecords.length}</span>
            <span className="mk-count-label">bibazo</span>
          </div>
        </div>
      )}

      {isMobile && (
        <div className="mk-mobile-tabs">
          <button
            className={`mk-tab mk-ripple ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <i className="bi bi-graph-up" />
            <span>Ibisobanuro</span>
          </button>
          <button
            className={`mk-tab mk-ripple ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            <i className="bi bi-list-ul" />
            <span>Ibibazo</span>
            {filteredRecords.length > 0 && <span className="mk-tab-badge">{filteredRecords.length}</span>}
          </button>
          <button
            className={`mk-tab mk-ripple ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => {
              setIsModalOpen(true);
              setActiveTab('add');
            }}
          >
            <i className="bi bi-plus-circle" />
            <span>Andika</span>
          </button>
        </div>
      )}

      {isMobile && activeTab === 'list' && (
        <div className="mk-mobile-filter">
          <div className="mk-filter-label">
            <i className="bi bi-funnel" />
            <span>Hitamo:</span>
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Ibibazo Byose ({records.length})</option>
            {statusOptions.map((opt) => {
              const count = records.filter((r) => r.imiterere === opt.value).length;
              return (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({count})
                </option>
              );
            })}
          </select>
          <span className="mk-filter-count">{filteredRecords.length}</span>
        </div>
      )}

      <main className="mk-main-content">
        {(activeTab === 'stats' || !isMobile) && (
          <section className="mk-stats-section">
            <div className="mk-section-header">
              <h2>
                <i className="bi bi-bar-chart-line" />
                Ibisobanuro by'Amakimbirane
              </h2>
              <span className="mk-section-count">{records.length} Byose</span>
            </div>
            <GraphicalStats records={records} />
          </section>
        )}

        {(activeTab === 'list' || !isMobile) && (
          <section className="mk-list-section">
            <div className="mk-section-header">
              <h2>
                <i className="bi bi-card-list" />
                Lisiti y'Amakimbirane
              </h2>
              <span className="mk-section-count">{filteredRecords.length}</span>
            </div>

            {loading ? (
              <div className="mk-loader">
                <div className="mk-spinner" />
                <p>Gutegereza amakuru...</p>
                <small>Kuramo amakuru kuri seriveri</small>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="mk-empty">
                <div className="mk-empty-icon">
                  <i className="bi bi-inbox" />
                </div>
                <h3>Nta macyimbirane yabonetse</h3>
                <p>
                  {filterStatus === 'all'
                    ? 'Nta makuru yabonetse. Kanda hano hejuru kugirango wandike amakimbirane mashya.'
                    : `Nta macyimbirane "${statusOptions.find((s) => s.value === filterStatus)?.label}" yabonetse.`}
                </p>
                {filterStatus !== 'all' && (
                  <button className="mk-empty-btn" onClick={() => setFilterStatus('all')}>
                    <i className="bi bi-arrow-left" />
                    Reba byose
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="mk-list-count">
                  <i className="bi bi-check2-circle" />
                  Byerekanwe: <strong>{filteredRecords.length}</strong> kuri <strong>{records.length}</strong>
                </div>
                <div className={isMobile ? 'mk-card-list' : 'mk-grid'}>
                  {filteredRecords.map((record) => (
                    <ConflictCard key={record.id} record={record} onEdit={handleEdit} onDelete={handleDeleteRequest} />
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </main>

      {isMobile && activeTab === 'list' && (
        <button
          className="mk-fab mk-ripple"
          onClick={() => {
            setIsModalOpen(true);
            setActiveTab('add');
          }}
          aria-label="Andika amakimbirane"
        >
          <i className="bi bi-plus-lg" />
        </button>
      )}

      {/* FORM MODAL */}
      {isModalOpen && createPortal(
        <div className="mk-modal-overlay" onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div className="mk-modal">
            <div className="mk-modal-header">
              <h2>
                <i className={`bi ${editingId ? 'bi-pencil-square' : 'bi-plus-circle'}`} />
                {editingId ? 'Hindura Amakuru' : 'Andika Amakimbirane'}
              </h2>
              <button className="mk-close-modal mk-ripple" onClick={resetForm}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <ResponsiveForm
              formData={formData}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onCancel={resetForm}
              editingId={editingId}
              platform={platform}
              loading={submitLoading}
              householdHeads={householdHeads}
            />
          </div>
        </div>,
        document.body
      )}

      {/* DELETE CONFIRMATION MODAL */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        recordName={deleteModal.recordName}
        isDeleting={deleteModal.isDeleting}
      />
    </div>
  );
}

const MkStyles = () => (
  <style>{`
    * {
      -webkit-tap-highlight-color: transparent;
      box-sizing: border-box;
    }

    .mk-container {
      --primary: #0B3D91;
      --primary-dark: #093278;
      --primary-light: #1e56b8;
      --danger: #dc2626;
      --danger-dark: #b91c1c;
      --danger-light: #ef4444;
      --warning: #d97706;
      --success: #16a34a;

      --bg-body: #f0f4f8;
      --bg-card: #ffffff;
      --bg-card-hover: #f0f4fb;
      --bg-elevated: #eef3fb;
      --bg-overlay: rgba(11,61,145,0.55);

      --text-main: #0d1b2a;
      --text-muted: #64748b;
      --text-dim: #94a3b8;

      --border-color: rgba(11,61,145,0.1);
      --border-light: rgba(11,61,145,0.14);
      --border-medium: rgba(11,61,145,0.2);

      --radius-sm: 8px;
      --radius-md: 12px;
      --radius-lg: 16px;
      --radius-xl: 20px;
      --radius-2xl: 24px;

      --space-xs: 0.25rem;
      --space-sm: 0.5rem;
      --space-md: 1rem;
      --space-lg: 1.5rem;
      --space-xl: 2rem;
      --space-2xl: 3rem;

      --text-xs: 0.75rem;
      --text-sm: 0.875rem;
      --text-base: 1rem;
      --text-lg: 1.125rem;
      --text-xl: 1.25rem;
      --text-2xl: 1.5rem;
      --text-3xl: 2rem;

      --shadow-sm: 0 1px 3px rgba(0,0,0,0.2);
      --shadow-md: 0 4px 12px rgba(0,0,0,0.3);
      --shadow-lg: 0 8px 25px rgba(0,0,0,0.4);
      --shadow-xl: 0 20px 40px rgba(0,0,0,0.5);

      --touch-target-sm: 44px;
      --touch-target-md: 48px;
      --touch-target-lg: 56px;

      color: var(--text-main);
      min-height: 100vh;
      background: var(--bg-body);
      padding: var(--space-md);
      padding-bottom: 120px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      line-height: 1.6;
    }

    /* ===============================================
       DELETE CONFIRMATION MODAL STYLES
       =============================================== */
    .mk-delete-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.9);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      padding: var(--space-md);
      animation: fadeIn 0.3s ease;
    }

    .mk-delete-modal {
      background: var(--bg-card);
      border: 1px solid var(--border-light);
      border-radius: var(--radius-xl);
      max-width: 480px;
      width: 100%;
      overflow: hidden;
      box-shadow: var(--shadow-xl);
      animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      transform-origin: center;
    }

    .mk-delete-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: var(--space-xl) var(--space-lg) var(--space-lg);
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
      border-bottom: 1px solid var(--border-color);
    }

    .mk-delete-icon {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--danger), var(--danger-dark));
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: var(--space-lg);
      font-size: var(--text-3xl);
      color: white;
      box-shadow: var(--shadow-lg);
      animation: pulse 2s infinite;
    }

    .mk-delete-title {
      font-size: var(--text-xl);
      font-weight: 800;
      margin: 0;
      color: var(--text-main);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .mk-delete-content {
      padding: var(--space-xl) var(--space-lg);
      text-align: center;
    }

    .mk-delete-message {
      font-size: var(--text-base);
      line-height: 1.6;
      color: var(--text-main);
      margin: 0 0 var(--space-lg);
    }

    .mk-delete-message strong {
      color: var(--danger);
      font-weight: 700;
      background: rgba(239, 68, 68, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
    }

    .mk-delete-warning {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-sm);
      padding: var(--space-md);
      background: rgba(239, 68, 68, 0.08);
      border: 1px solid rgba(239, 68, 68, 0.2);
      border-left: 4px solid var(--danger);
      border-radius: var(--radius-md);
      font-size: var(--text-sm);
      color: var(--danger);
      font-weight: 600;
    }

    .mk-delete-warning i {
      font-size: var(--text-lg);
      flex-shrink: 0;
    }

    .mk-delete-actions {
      display: flex;
      gap: var(--space-md);
      padding: var(--space-lg);
      background: var(--bg-elevated);
      border-top: 1px solid var(--border-color);
    }

    .mk-delete-btn {
      flex: 1;
      min-height: var(--touch-target-lg);
      border-radius: var(--radius-md);
      border: none;
      font-size: var(--text-base);
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-sm);
      cursor: pointer;
      transition: all 0.2s ease;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .mk-delete-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }

    .mk-delete-cancel {
      background: var(--bg-card-hover);
      color: var(--text-main);
      border: 2px solid var(--border-light);
    }

    .mk-delete-cancel:hover:not(:disabled) {
      background: var(--text-muted);
      color: var(--bg-body);
      border-color: var(--text-muted);
      transform: translateY(-1px);
    }

    .mk-delete-cancel:active:not(:disabled) {
      transform: scale(0.95);
    }

    .mk-delete-confirm {
      background: linear-gradient(135deg, var(--danger), var(--danger-dark));
      color: white;
      border: 2px solid var(--danger);
      box-shadow: var(--shadow-md);
      position: relative;
      overflow: hidden;
    }

    .mk-delete-confirm:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--danger-dark), #b91c1c);
      border-color: var(--danger-dark);
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }

    .mk-delete-confirm:active:not(:disabled) {
      transform: scale(0.95);
    }

    .mk-delete-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    /* DELETE ANIMATIONS */
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes scaleIn {
      from { 
        opacity: 0;
        transform: scale(0.8);
      }
      to { 
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes pulse {
      0%, 100% { 
        box-shadow: var(--shadow-lg);
      }
      50% { 
        box-shadow: 0 8px 25px rgba(239, 68, 68, 0.6);
      }
    }

    /* Mobile Responsive for Delete Modal */
    @media (max-width: 479px) {
      .mk-delete-modal {
        max-width: 95vw;
        margin: var(--space-sm);
      }

      .mk-delete-icon {
        width: 64px;
        height: 64px;
        font-size: var(--text-2xl);
      }

      .mk-delete-title {
        font-size: var(--text-lg);
      }

      .mk-delete-actions {
        flex-direction: column;
        gap: var(--space-sm);
      }

      .mk-delete-btn {
        min-height: 52px;
        font-size: var(--text-sm);
      }
    }

    /* ===============================================
       EXISTING STYLES (ALL PREVIOUS STYLES REMAIN THE SAME)
       =============================================== */
    
    .mk-ripple {
      position: relative;
      overflow: hidden;
    }

    .mk-mobile-nav {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--space-md);
      background: var(--bg-card);
      border-radius: var(--radius-lg);
      margin-bottom: var(--space-md);
      position: sticky;
      top: 0;
      z-index: 100;
      box-shadow: var(--shadow-md);
      border: 1px solid var(--border-light);
    }

    .mk-nav-brand {
      display: flex;
      align-items: center;
      gap: var(--space-md);
      background: none;
      border: none;
      color: var(--text-main);
      padding: 0;
      cursor: pointer;
    }

    .mk-nav-logo {
      width: 44px;
      height: 44px;
      min-width: 44px;
      background: linear-gradient(135deg, var(--primary), var(--primary-dark));
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--text-xl);
      color: white;
    }

    .mk-nav-main {
      display: block;
      font-size: var(--text-base);
      font-weight: 700;
    }

    .mk-nav-sub {
      display: block;
      font-size: var(--text-xs);
      color: var(--text-muted);
    }

    .mk-nav-btn {
      width: var(--touch-target-md);
      height: var(--touch-target-md);
      background: var(--bg-card-hover);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      color: var(--text-main);
      font-size: var(--text-xl);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .mk-nav-overlay {
      position: fixed;
      inset: 0;
      background: var(--bg-overlay);
      z-index: 1000;
    }

    .mk-nav-drawer {
      position: fixed;
      top: 0;
      left: 0;
      width: min(320px, 85vw);
      height: 100vh;
      background: var(--bg-card);
      z-index: 1001;
      box-shadow: var(--shadow-xl);
      display: flex;
      flex-direction: column;
    }

    .mk-drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-lg);
      border-bottom: 1px solid var(--border-color);
      background: var(--bg-elevated);
      font-weight: 700;
    }

    .mk-drawer-content {
      padding: var(--space-md);
      overflow-y: auto;
    }

    .mk-drawer-item {
      width: 100%;
      display: flex;
      align-items: center;
      gap: var(--space-md);
      padding: var(--space-md);
      background: none;
      border: none;
      border-radius: var(--radius-md);
      color: var(--text-main);
      cursor: pointer;
      text-align: left;
      margin-bottom: var(--space-sm);
    }

    .mk-drawer-item.active {
      background: var(--bg-card-hover);
      color: var(--primary);
    }

    .mk-drawer-arrow {
      margin-left: auto;
    }

    .mk-mobile-tabs {
      display: flex;
      gap: var(--space-sm);
      padding: var(--space-sm);
      background: var(--bg-card);
      border-radius: var(--radius-lg);
      position: fixed;
      bottom: var(--space-md);
      left: var(--space-md);
      right: var(--space-md);
      z-index: 99;
      box-shadow: var(--shadow-lg);
      border: 1px solid var(--border-light);
    }

    .mk-tab {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-xs);
      padding: var(--space-md) var(--space-sm);
      background: none;
      border: none;
      border-radius: var(--radius-md);
      color: var(--text-muted);
      font-size: var(--text-xs);
      cursor: pointer;
      position: relative;
      min-height: var(--touch-target-lg);
    }

    .mk-tab.active {
      background: var(--primary);
      color: white;
    }

    .mk-tab-badge {
      position: absolute;
      top: 6px;
      right: 6px;
      background: var(--danger);
      color: white;
      font-size: 10px;
      border-radius: 999px;
      padding: 2px 6px;
    }

    .mk-mobile-filter,
    .mk-filter-bar,
    .mk-section-header,
    .mk-list-count,
    .mk-chart-section,
    .mk-pie-section,
    .mk-stat-card,
    .mk-card,
    .mk-header {
      background: var(--bg-card);
      border: 1px solid var(--border-light);
    }

    .mk-mobile-filter {
      display: flex;
      gap: var(--space-md);
      align-items: center;
      padding: var(--space-md);
      border-radius: var(--radius-md);
      margin-bottom: var(--space-md);
    }

    .mk-mobile-filter select,
    .mk-search-wrapper select {
      background: var(--bg-card-hover);
      color: var(--text-main);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-sm);
      padding: 12px;
    }

    .mk-filter-count {
      color: var(--primary);
      font-weight: 700;
    }

    .mk-section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-xl);
      padding: var(--space-md);
      border-radius: var(--radius-lg);
      border-left: 4px solid var(--primary);
    }

    .mk-section-header h2 {
      margin: 0;
      font-size: var(--text-xl);
      display: flex;
      align-items: center;
      gap: var(--space-sm);
    }

    .mk-section-count {
      background: var(--primary);
      color: white;
      padding: 6px 12px;
      border-radius: var(--radius-md);
      font-weight: 700;
    }

    .mk-stats-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--space-xl);
    }

    .mk-stats-grid {
      display: grid;
      gap: var(--space-md);
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }

    .mk-stat-card {
      border-radius: var(--radius-lg);
      padding: var(--space-lg);
    }

    .mk-stat-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: var(--space-md);
    }

    .mk-stat-icon {
      width: 48px;
      height: 48px;
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mk-stat-value {
      font-size: var(--text-3xl);
      font-weight: 800;
    }

    .mk-stat-label {
      color: var(--text-muted);
      margin-bottom: var(--space-md);
    }

    .mk-stat-progress {
      height: 8px;
      background: var(--border-color);
      border-radius: 999px;
      overflow: hidden;
    }

    .mk-stat-progress-bar {
      height: 100%;
    }

    .mk-chart-section,
    .mk-pie-section {
      border-radius: var(--radius-xl);
      padding: var(--space-xl);
    }

    .mk-chart-title {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      margin-top: 0;
      margin-bottom: var(--space-lg);
    }

    .mk-chart-row {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: var(--space-md);
      align-items: center;
      margin-bottom: var(--space-md);
    }

    .mk-chart-label {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      min-width: 120px;
    }

    .mk-chart-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
    }

    .mk-chart-bar-wrapper {
      height: 36px;
      background: var(--border-color);
      border-radius: var(--radius-sm);
      overflow: hidden;
    }

    .mk-chart-bar {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
    }

    .mk-pie-container {
      display: flex;
      flex-direction: column;
      gap: var(--space-xl);
      align-items: center;
    }

    .mk-pie-chart {
      position: relative;
      width: 240px;
      height: 240px;
    }

    .mk-pie-svg {
      width: 100%;
      height: 100%;
      transform: rotate(-90deg);
    }

    .mk-pie-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
    }

    .mk-pie-total {
      display: block;
      font-size: var(--text-3xl);
      font-weight: 800;
    }

    .mk-pie-label {
      font-size: var(--text-xs);
      color: var(--text-muted);
    }

    .mk-pie-legend {
      width: 100%;
      max-width: 320px;
    }

    .mk-pie-legend-item {
      display: flex;
      align-items: center;
      gap: var(--space-md);
      padding: var(--space-md) 0;
      border-bottom: 1px solid var(--border-color);
    }

    .mk-legend-color {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      flex-shrink: 0;
    }

    .mk-legend-label {
      flex: 1;
    }

    .mk-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(min(350px, 100%), 1fr));
      gap: var(--space-xl);
    }

    .mk-card-list {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
    }

    .mk-card {
      border-radius: var(--radius-xl);
      padding: var(--space-lg);
    }

    .mk-card-header {
      display: flex;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-lg);
    }

    .mk-card-user {
      display: flex;
      gap: var(--space-md);
      flex: 1;
      min-width: 0;
    }

    .mk-card-avatar {
      width: 52px;
      height: 52px;
      min-width: 52px;
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mk-card-user-info h4 {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mk-card-date {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--text-dim);
      font-size: var(--text-sm);
      margin-top: 4px;
    }

    .mk-card-status {
      border: 1px solid;
      border-radius: var(--radius-md);
      padding: 8px 10px;
      font-size: var(--text-xs);
      font-weight: 700;
      white-space: nowrap;
    }

    .mk-card-body {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
      margin-bottom: var(--space-lg);
    }

    .mk-card-item {
      background: var(--bg-card-hover);
      padding: var(--space-md);
      border-radius: var(--radius-md);
      border: 1px solid var(--border-color);
    }

    .mk-card-item label {
      display: flex;
      gap: 6px;
      align-items: center;
      margin-bottom: 8px;
      font-size: var(--text-xs);
      font-weight: 700;
      color: var(--text-dim);
      text-transform: uppercase;
    }

    .mk-card-item p {
      margin: 0;
      word-break: break-word;
    }

    .mk-card-solution {
      border-left: 4px solid var(--success);
    }

    .mk-text-clamp {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .mk-expand-btn {
      width: 100%;
      padding: 12px;
      border: 1px dashed var(--border-light);
      background: var(--bg-card-hover);
      color: var(--primary);
      border-radius: var(--radius-md);
      cursor: pointer;
    }

    .mk-card-actions {
      display: flex;
      gap: var(--space-md);
      padding-top: var(--space-lg);
      border-top: 1px solid var(--border-color);
    }

    .mk-btn {
      flex: 1;
      min-height: 48px;
      border-radius: var(--radius-md);
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 600;
    }

    .mk-btn-edit {
      background: var(--bg-card-hover);
      color: var(--text-main);
      border: 1px solid var(--border-light);
    }

    .mk-btn-delete {
      background: rgba(239, 68, 68, 0.12);
      color: var(--danger);
      border: 1px solid rgba(239, 68, 68, 0.3);
    }

    .mk-fab {
      position: fixed;
      bottom: 100px;
      right: 20px;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      border: none;
      background: var(--primary);
      color: white;
      font-size: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: var(--shadow-lg);
      z-index: 98;
      cursor: pointer;
    }

    .mk-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(4, 12, 28, 0.72);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      display: flex;
      align-items: flex-end;
      justify-content: center;
      z-index: 2000;
    }

    .mk-modal {
      background: var(--bg-card);
      width: 100%;
      max-width: 720px;
      height: 100vh;
      max-height: 100vh;
      display: flex;
      flex-direction: column;
      border-radius: 18px 18px 0 0;
      overflow: hidden;
    }

    .mk-modal-header {
      padding: var(--space-lg);
      border-bottom: 1px solid var(--border-color);
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: var(--bg-elevated);
      flex-shrink: 0;
    }

    .mk-modal-header h2 {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: var(--text-lg);
    }

    .mk-close-modal {
      width: 44px;
      height: 44px;
      border: none;
      border-radius: var(--radius-md);
      background: var(--bg-card-hover);
      color: var(--text-main);
      font-size: 20px;
      cursor: pointer;
    }

    .mk-form {
      flex: 1;
      overflow-y: auto;
      padding: var(--space-lg);
      padding-bottom: 140px;
      -webkit-overflow-scrolling: touch;
      max-width: 680px;
      margin: 0 auto;
      width: 100%;
    }

    .mk-form-header {
      padding-bottom: var(--space-md);
      margin-bottom: var(--space-md);
      border-bottom: 1px solid var(--border-color);
    }

    .mk-form-progress {
      width: 100%;
      height: 4px;
      background: var(--border-color);
      border-radius: 999px;
      overflow: hidden;
      margin-bottom: var(--space-md);
    }

    .mk-progress-bar {
      height: 100%;
      background: linear-gradient(90deg, var(--primary), var(--primary-light));
    }

    .mk-auto-save-status {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: var(--text-xs);
      color: var(--text-muted);
    }

    .mk-form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--space-md);
    }

    .mk-input-group.mk-full {
      grid-column: 1 / -1;
    }

    @media (max-width: 480px) {
      .mk-form-grid { grid-template-columns: 1fr; }
      .mk-input-group.mk-full { grid-column: 1; }
    }

    .mk-input-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .mk-input-group label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: var(--text-sm);
      color: var(--text-muted);
      font-weight: 600;
      flex-wrap: wrap;
    }

    .mk-required {
      color: var(--danger);
    }

    .mk-optional {
      color: var(--text-dim);
      font-size: var(--text-xs);
    }

    .mk-input-group input,
    .mk-input-group select,
    .mk-input-group textarea {
      width: 100%;
      padding: 11px 14px;
      border-radius: var(--radius-md);
      border: 2px solid var(--border-color);
      background: var(--bg-card-hover);
      color: var(--text-main);
      font-size: 15px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
      font-family: inherit;
    }

    .mk-input-group textarea {
      min-height: 90px;
      resize: vertical;
    }

    .mk-input-group select {
      cursor: pointer;
    }

    .mk-input-group input:focus,
    .mk-input-group select:focus,
    .mk-input-group textarea:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
    }

    .mk-input-group input.error,
    .mk-input-group select.error,
    .mk-input-group textarea.error {
      border-color: var(--danger);
    }

    .mk-field-error {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--danger);
      font-size: var(--text-xs);
      margin-top: 8px;
      background: rgba(239, 68, 68, 0.08);
      padding: 8px 10px;
      border-radius: var(--radius-sm);
    }

    .mk-textarea-footer {
      display: flex;
      justify-content: space-between;
      gap: var(--space-md);
      flex-wrap: wrap;
      margin-top: 8px;
    }

    .mk-char-count {
      font-size: var(--text-xs);
      color: var(--text-dim);
    }

    .mk-radio-group {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: var(--space-sm);
    }

    @media (max-width: 480px) {
      .mk-radio-group { grid-template-columns: 1fr; }
    }

    .mk-radio-label {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      padding: var(--space-md);
      background: var(--bg-card-hover);
      border: 2px solid var(--border-color);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: border-color 0.2s, background 0.2s;
    }

    .mk-radio-label.active {
      background: var(--radio-bg);
      border-color: var(--radio-color);
    }

    .mk-radio-label input {
      display: none;
    }

    .mk-radio-icon {
      width: 36px;
      height: 36px;
      min-width: 36px;
      border-radius: 50%;
      background: var(--bg-card);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-muted);
      font-size: 16px;
      flex-shrink: 0;
    }

    .mk-radio-label.active .mk-radio-icon {
      background: var(--radio-color);
      color: white;
    }

    .mk-radio-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .mk-radio-text {
      font-weight: 700;
    }

    .mk-radio-desc {
      font-size: var(--text-sm);
      color: var(--text-muted);
    }

    .mk-form-actions {
      max-width: 680px;
      margin: var(--space-lg) auto 0;
      width: 100%;
      padding: var(--space-md) 0 var(--space-lg);
      border-top: 1px solid var(--border-color);
      display: flex;
      gap: var(--space-md);
    }

    .mk-btn-cancel,
    .mk-btn-submit {
      min-height: 48px;
      border-radius: var(--radius-md);
      font-size: var(--text-sm);
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      letter-spacing: 0.3px;
      transition: all 0.2s ease;
      text-transform: uppercase;
    }

    .mk-btn-cancel {
      flex: 1;
      background: transparent;
      color: var(--text-muted);
      border: 1.5px solid var(--border-medium);
    }

    .mk-btn-cancel:hover {
      background: var(--bg-card-hover);
      color: var(--text-main);
      border-color: var(--text-dim);
    }

    .mk-btn-cancel:active {
      transform: scale(0.97);
    }

    .mk-btn-submit {
      flex: 2;
      background: linear-gradient(135deg, var(--primary), var(--primary-dark));
      color: white;
      border: none;
      box-shadow: 0 4px 14px rgba(124, 58, 237, 0.35);
    }

    .mk-btn-submit:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--primary-light), var(--primary));
      box-shadow: 0 6px 20px rgba(124, 58, 237, 0.5);
      transform: translateY(-1px);
    }

    .mk-btn-submit:active:not(:disabled) {
      transform: scale(0.97) translateY(0);
      box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
    }

    .mk-btn-submit.disabled {
      opacity: 0.45;
      cursor: not-allowed;
      box-shadow: none;
    }

    .mk-loading-spinner,
    .mk-spinner,
    .mk-pull-spinner {
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    .mk-loading-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255,255,255,0.35);
      border-top-color: white;
    }

    .mk-loader,
    .mk-empty {
      text-align: center;
      padding: var(--space-2xl) var(--space-lg);
      color: var(--text-muted);
    }

    .mk-spinner {
      width: 48px;
      height: 48px;
      border: 3px solid var(--border-color);
      border-top-color: var(--primary);
      margin: 0 auto var(--space-lg);
    }

    .mk-empty-icon {
      font-size: 70px;
      opacity: 0.3;
      margin-bottom: var(--space-lg);
    }

    .mk-empty-btn {
      background: var(--primary);
      color: white;
      border: none;
      padding: 12px 16px;
      border-radius: var(--radius-md);
      cursor: pointer;
    }

    .mk-draft-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      padding: var(--space-md);
    }

    .mk-draft-dialog {
      width: 100%;
      max-width: 400px;
      background: var(--bg-card);
      border-radius: var(--radius-xl);
      padding: var(--space-xl);
      text-align: center;
    }

    .mk-draft-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: var(--space-md);
    }

    .mk-draft-actions {
      display: flex;
      gap: var(--space-md);
      justify-content: center;
      flex-wrap: wrap;
    }

    .mk-draft-btn {
      min-height: 44px;
      padding: 10px 16px;
      border-radius: var(--radius-md);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .mk-draft-reject {
      background: var(--bg-card-hover);
      color: var(--text-main);
      border: 1px solid var(--border-color);
    }

    .mk-draft-accept {
      background: var(--primary);
      color: white;
    }

    .mk-pull-indicator {
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 999;
      background: var(--bg-card);
      padding: var(--space-md);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      text-align: center;
    }

    .mk-pull-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-color);
      border-top-color: var(--primary);
      margin: 0 auto 8px;
    }

    .mk-pull-text {
      margin: 0;
      font-size: var(--text-xs);
    }

    .mk-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-xl);
      padding: var(--space-xl);
      border-radius: var(--radius-2xl);
    }

    .mk-header-info h1 {
      margin: 0;
      font-size: var(--text-2xl);
      display: flex;
      align-items: center;
      gap: var(--space-md);
    }

    .mk-header-info p {
      margin-top: 8px;
      color: var(--text-muted);
    }

    .mk-add-btn {
      background: linear-gradient(135deg, var(--primary), var(--primary-dark));
      color: white;
      border: none;
      padding: 14px 20px;
      border-radius: var(--radius-md);
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }

    .mk-filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--space-lg);
      margin-bottom: var(--space-xl);
      padding: var(--space-lg);
      border-radius: var(--radius-lg);
    }

    .mk-search-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .mk-results-count {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .mk-count-number {
      color: var(--primary);
      font-weight: 800;
      font-size: var(--text-xl);
    }

    @media (min-width: 768px) {
      .mk-container { padding: var(--space-xl); padding-bottom: var(--space-xl); max-width: 1400px; margin: 0 auto; }
      .mk-modal-overlay { align-items: center; padding: var(--space-lg); }
      .mk-modal { height: auto; max-height: 92vh; border-radius: var(--radius-xl); }
      .mk-form { padding-bottom: var(--space-xl); }
      .mk-pie-container { flex-direction: row; justify-content: space-around; align-items: center; }
      .mk-main-content { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-xl); align-items: start; }
      .mk-stats-section { grid-column: 1 / -1; }
      .mk-header { flex-wrap: wrap; gap: var(--space-md); }
      .mk-filter-bar { flex-wrap: wrap; }
      .mk-stats-grid { grid-template-columns: repeat(3, 1fr); }
      .mk-chart-label { min-width: 110px; }
    }

    @media (min-width: 1024px) {
      .mk-main-content { grid-template-columns: 400px 1fr; }
      .mk-stats-section { grid-column: 1 / -1; }
    }

    @media (max-width: 767px) {
      .mk-main-content { padding-bottom: 110px; display: flex; flex-direction: column; gap: var(--space-lg); }
      .mk-modal { height: 100dvh; max-height: 100dvh; border-radius: 18px 18px 0 0; }
      .mk-form { padding: 16px; padding-bottom: 160px; }
      .mk-header { flex-direction: column; align-items: stretch; gap: var(--space-md); }
      .mk-add-btn { width: 100%; justify-content: center; }
      .mk-filter-bar { flex-direction: column; align-items: stretch; gap: var(--space-sm); }
      .mk-search-wrapper { width: 100%; }
      .mk-search-wrapper select { width: 100%; }
      .mk-section-header { flex-wrap: wrap; gap: var(--space-sm); }
      .mk-section-header h2 { font-size: var(--text-base); }
      .mk-stats-grid { grid-template-columns: 1fr 1fr; }
      .mk-chart-section, .mk-pie-section { padding: var(--space-md); }
    }

    @media (max-width: 479px) {
      .mk-container { padding: 10px; padding-bottom: 110px; }
      .mk-mobile-tabs { left: 10px; right: 10px; bottom: 10px; }
      .mk-fab { right: 14px; bottom: 90px; }
      .mk-card-header { flex-direction: column; align-items: flex-start; }
      .mk-card-status { align-self: flex-start; }
      .mk-card { padding: var(--space-md); }
      .mk-card-avatar { width: 40px; height: 40px; min-width: 40px; }
      .mk-chart-row { grid-template-columns: 1fr; gap: 6px; }
      .mk-chart-label { min-width: auto; }
      .mk-pie-chart { width: 160px; height: 160px; }
      .mk-stats-grid { grid-template-columns: 1fr; }
      .mk-stat-card { padding: var(--space-md); }
      .mk-stat-value { font-size: var(--text-2xl); }
      .mk-mobile-filter { flex-wrap: wrap; }
      .mk-mobile-filter select { flex: 1; min-width: 0; }
      .mk-btn-cancel, .mk-btn-submit { width: 100%; min-height: 50px; }
      .mk-radio-label { align-items: flex-start; }
      .mk-section-header h2 { font-size: var(--text-sm); }
    }

    @supports (padding: env(safe-area-inset-bottom)) {
      .mk-container { padding-bottom: calc(120px + env(safe-area-inset-bottom)); }
      .mk-mobile-tabs { bottom: max(12px, env(safe-area-inset-bottom)); left: max(12px, env(safe-area-inset-left)); right: max(12px, env(safe-area-inset-right)); }
      .mk-fab { bottom: calc(95px + env(safe-area-inset-bottom)); right: max(16px, env(safe-area-inset-right)); }
      .mk-form { padding-bottom: calc(170px + env(safe-area-inset-bottom)); }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}</style>
);